import React, { useState, useEffect, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Grid, FormControl, FormHelperText, InputLabel, Select, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { departmentDetail, updateDepartment } from '../../API';

const ModalEditDepartment = ({ onClick, id, onDataUpdate, departmentList }) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentDepartment, setParentDepartment] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [codeError, setCodeError] = useState('');
  const [nameError, setNameError] = useState('');
  const [parentDepartmentError, setParentDepartmentError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const topLevelDepartments = (departmentList || []).filter(
    (item) => !item.under_department_id && item.id !== id
  );

  const getDetail = useCallback(async () => {
    try {
      const response = await departmentDetail(id);
      if (response?.meta.code === 200) {
        const data = response?.data;
        setCode(data?.code || '');
        setName(data?.name || '');
        setIsActive(data?.inactive_at === null);
        const parent = (departmentList || []).find((item) => item.id === data?.under_department_id);
        setParentDepartment(parent || null);
      }
    } catch (error) {
      console.log(error);
    }
  }, [id, departmentList]);

  useEffect(() => {
    getDetail();
  }, [getDetail]);

  const handleUpdate = async () => {
    setCodeError('');
    setNameError('');
    setParentDepartmentError('');
    setIsLoading(true);

    if (!code) {
      setCodeError('Kode harus diisi');
      setIsLoading(false);
      return;
    }
    if (!name) {
      setNameError('Nama harus diisi');
      setIsLoading(false);
      return;
    }

    const body = {
      code,
      name,
      under_department_id: parentDepartment?.id ?? null,
      is_active: isActive ? 1 : 0,
    };

    try {
      const res = await updateDepartment(id, body);
      if (res.meta.code === 200) {
        toast.success('Departemen berhasil diubah.');
        onDataUpdate();
        onClick();
      }
    } catch (error) {
      const errors = error.response?.data?.meta?.errors;
      if (errors) {
        if (errors.code) setCodeError(errors.code[0]);
        if (errors.name) setNameError(errors.name[0]);
        if (errors.under_department_id) setParentDepartmentError(errors.under_department_id[0]);
        Object.values(errors)
          .flat()
          .forEach((msg) => toast.error(msg));
      } else {
        toast.error(error.response?.data?.meta?.message || 'Gagal mengubah departemen');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} lg={6}>
        <TextField
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          id="outlined-required"
          label="Kode"
          fullWidth
          error={!!codeError}
          helperText={codeError}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          id="outlined-required"
          label="Nama"
          fullWidth
          error={!!nameError}
          helperText={nameError}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth error={!!parentDepartmentError}>
          <Autocomplete
            id="parent-department-autocomplete"
            options={topLevelDepartments}
            value={parentDepartment}
            onChange={(event, newValue) => setParentDepartment(newValue)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Departemen Induk (opsional)" error={!!parentDepartmentError} fullWidth />
            )}
          />
          {parentDepartmentError && <FormHelperText>{parentDepartmentError}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth>
          <InputLabel required id="department-status-label">
            Status
          </InputLabel>
          <Select
            required
            labelId="department-status-label"
            value={isActive}
            label="Status"
            onChange={(event) => setIsActive(event.target.value)}
          >
            <MenuItem value={true}>Aktif</MenuItem>
            <MenuItem value={false}>Tidak Aktif</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} lg={12}>
        <LoadingButton
          loading={isLoading}
          role="CircularProgress"
          color="color"
          variant="contained"
          fullWidth
          size="large"
          onClick={handleUpdate}
        >
          SIMPAN
        </LoadingButton>
      </Grid>
    </Grid>
  );
};

export default ModalEditDepartment;
